import React, { useState, useEffect, useRef, Fragment } from "react";
import { useLocation, useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Container, Row, Col } from 'react-bootstrap';
import { Button, Tooltip } from '@mui/material';
import { InfoOutlined as InfoOutlinedIcon } from '@mui/icons-material';
import { AddCircleOutlineOutlined as AddCircleOutlineOutlinedIcon } from '@mui/icons-material';
import { RemoveCircleOutlineOutlined as RemoveCircleOutlineOutlinedIcon } from '@mui/icons-material';
import {
  getSessionStorageItem,
  setSessionStorageItem,
  SESSION_STORAGE_ACCOUNT_KEY,
  SESSION_STORAGE_ACTIVE_PURL,
  SESSION_STORAGE_FINAL_EXPENSE_TERM_KEY,
  SESSION_STORAGE_FINAL_EXPENSE_SORT_FILTER_KEY,
  getBasicInfo,
  setBasicInfo,
} from '../../../../sessionStorage';
import NoQuoteAvailable from "../../Common/SubComponents/NoQuoteAvailable";
import { ContactSpecialist } from "../Common/ContactSpecialist";
import { saveSessionEvent } from '../../../../session';
import ProductDetails from "../../FullUW/Helpers/ProductDetails";
import LoadingGrid from '../Helpers/LoadingGrid';
import BasicInfoSummaryFinalExpenseModel from "../../Common/SubComponents/BasicInfoSummaryFinalExpenseModel";
import Age from "../../../Helpers/Dates/Age";
import CoverageAmountByAgeBand from "../Helpers/CoverageAmountByAgeBand";
import CtaSolution from '../Common/CtaSolution/CtaSolution'
import Testimonials from "../../../Common/Testimonials/Testimonials";
import BasicInfoSummaryExtended from '../QuoterComponents/BasicInfoSummaryExtended';
import FinalExpenseQuoteCard from "../QuoterComponents/FinalExpenseQuoteCard";
import DigitalCoverageAmounts from "../../../Helpers/Lists/CoverageAmounts/DigitalCoverageAmounts";
import QuoterFilter from '../Helpers/QuoterFilter';
import QuoterFiltersCondensed from "../QuoterComponents/QuoterFiltersCondensed";
import QuoterSortCondensed from "../QuoterComponents/QuoterSortCondensed";
import { GetFEXQuotes } from "../Common/GetFEXQuotes";

import "./Quoter.scss";

export default function Quoter(props) {

  const accountObj = getSessionStorageItem(SESSION_STORAGE_ACCOUNT_KEY);
  const finalExpenseSettings = getSessionStorageItem(SESSION_STORAGE_FINAL_EXPENSE_SORT_FILTER_KEY);

  const navigate = useNavigate();
  const location = useLocation();
  const filterRef = useRef();
  const sortRef = useRef();

  useEffect(() => { saveSessionEvent() }, [location]);

  const basicInfo = getBasicInfo();
  const coverageDefaults = {
    coverageAmount: 10000,
    coverageMonthlyRate: 15,
  };

  if (!basicInfo.birthdate && !basicInfo.fakeBirthdate) {
    navigate('/landing/final-expense', { replace: true });
  };

  const [loading, setLoading] = useState(true);
  const [quoteResults, setQuoteResults] = useState([]);
  const [displayAllQuotes, setDisplayAllQuotes] = useState(false);
  const [termLength, setTermLength] = useState(props.formState.coverageLength || "Lifetime Pay");
  const [isQuotingCoverageAmount, setIsQuotingCoverageAmount] = useState(true);
  const [coverageAmount, setCoverageAmount] = useState(coverageDefaults.coverageAmount);
  const [coverageMonthlyRate, setCoverageMonthlyRate] = useState(coverageDefaults.coverageMonthlyRate)
  const [healthClass, setHealthClass] = useState(props.formState.healthClass || props.preQuoteDetails.healthClass || 'PP');
  const [multiTermFilter, setMultiTermFilter] = useState(false);
  const [filteredQuoteResults, setFilteredQuoteResults] = useState([]);
  const [displayedQuoteResults, setDisplayedQuoteResults] = useState(filteredQuoteResults);
  const [fastestRate, setFastestRate] = useState('');
  const [errorMessage, setErrorMessage] = useState({ message: '', status: '' });
  const [basicInfoModalShow, setBasicInfoModalShow] = useState(false);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const getActivePurlData = getSessionStorageItem(SESSION_STORAGE_ACTIVE_PURL);
  const isActivePurl = getActivePurlData ? getActivePurlData.active : false;
  const [isPurlVerification, setIsPurlVerification] = useState(isActivePurl);

  const [isSortedByPrice, setSortedByPrice] = useState((finalExpenseSettings !== null && finalExpenseSettings.sortByPrice !== null) ? finalExpenseSettings.sortByPrice : true);
  const [filterObject, setFilterObject] = useState((finalExpenseSettings !== null && finalExpenseSettings.filters !== null) ? finalExpenseSettings.filters : null);
  const [sortObject, setSortObject] = useState((finalExpenseSettings !== null && finalExpenseSettings.sortBy !== null) ? finalExpenseSettings.sortBy : null);
  const [lowestPrice, setlowestPrice] = useState('');

  const [quoteLogId, setQuoteLogId] = useState(null);

  const quoteResultsValueProp = 'Defined by your answers, these options are ranked based on price, features and application process. Please note: all options offer a 10 to 30 day money back guarantee (or according to your state regulations) referred to as a "Free Look Period".';

  const isTestimonialPanelEnable = accountObj && accountObj.account && accountObj.account.isTestimonialPanelEnable;
  const promotedProducts = JSON.parse(accountObj.account.promotedProducts);
  const promotedProductCount = promotedProducts ? promotedProducts.filter(p => p.isEnabled).length : 0;

  const accountProductTypeSettings = props ? accountObj.accountProducts.find(product => product.productLandingUrl === '/landing/final-expense') : null;
  const accountLandingOverride = accountObj.accountOverrides && accountProductTypeSettings ?
    accountObj.accountOverrides.find(override => override.accountProductTypeId === accountProductTypeSettings.productTypeId) : null;
  const utmQuoterCarrierId = accountLandingOverride ? accountLandingOverride.quoterCarrierId : null;

  const age = Age(basicInfo.birthdate);
  const coverageAmountSpecs = CoverageAmountByAgeBand(age);

  const handleToolTip = (e) => {
    e.stopPropagation();
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth',
    });
  };

  const handlePromotedProduct = (ctaType, filter, redirectUri) => {
    if (ctaType === "filter" && filter) {
      const productFilter = filter;
      const obj = { ...filterObject };

      switch (productFilter) {
        case "digitalOnly":
          if (!obj[productFilter]) {
            filterRef.current.updateFilterState(productFilter);
          } else {
            filterRef.current.updateFilterState("allApplicationType");
          }
          break;
        case "phoneOnly":
          if (!obj[productFilter]) {
            filterRef.current.updateFilterState(productFilter);
          } else {
            filterRef.current.updateFilterState("allApplicationType");
          }
          break;
        case "simplifiedIssue":
          if (!obj[productFilter]) {
            filterRef.current.updateFilterState(productFilter);
          } else {
            filterRef.current.updateFilterState("allUnderwritingType");
          }
          break;
        case "guaranteedIssue":
          if (!obj[productFilter]) {
            filterRef.current.updateFilterState(productFilter);
          } else {
            filterRef.current.updateFilterState("allUnderwritingType");
          }
          break;
        case "levelDeathBenefit":
          if (!obj[productFilter]) {
            filterRef.current.updateFilterState(productFilter);
          } else {
            filterRef.current.updateFilterState("allDeathBenefitType");
          }
          break;
        case "gradedDeathBenefit":
          if (!obj[productFilter]) {
            filterRef.current.updateFilterState(productFilter);
          } else {
            filterRef.current.updateFilterState("allDeathBenefitType");
          }
          break;
        case "modifiedDeathBenefit":
          if (!obj[productFilter]) {
            filterRef.current.updateFilterState(productFilter);
          } else {
            filterRef.current.updateFilterState("allDeathBenefitType");
          }
          break;
        default:
          break;
      }
      setFilterObject(obj);
      scrollToTop();
    }

    if (ctaType === "redirect" && redirectUri) {
      navigate(redirectUri);
      scrollToTop();
    }
  };

  const sorter = (a, b, sortByField) => {
    switch (sortByField) {
      case 'lowestPrice':
        return a.monthlyRate - b.monthlyRate || b.totalUnweightedScore - a.totalUnweightedScore;
      case 'highestPrice':
        return b.monthlyRate - a.monthlyRate || a.totalUnweightedScore - b.totalUnweightedScore;
      case 'lowestCoverage':
        return a.coverageAmount - b.coverageAmount || b.totalUnweightedScore - a.totalUnweightedScore;
      case 'highestCoverage':
        return b.coverageAmount - a.coverageAmount || b.totalUnweightedScore - a.totalUnweightedScore;
      case 'speed':
        return b.speedScore - a.speedScore || a.monthlyRate - b.monthlyRate || b.totalUnweightedScore - a.totalUnweightedScore;
      case 'bestSellers':
        return
      case 'highestRated':
        return b.companyRatingsScore - a.companyRatingsScore || a.totalUnweightedScore - b.totalUnweightedScore;
      case 'fastest':
        return b.speedScore - a.speedScore || a.monthlyRate - b.monthlyRate || b.totalUnweightedScore - a.totalUnweightedScore;
      default:
        break;
    }
  };

  const quoteLogIdCallback = (quoteLogId) => {
    setQuoteLogId(quoteLogId);
  };

  const runQuote = (getIsSubscribed) => {
    setLoading(true);
    GetFEXQuotes(getBasicInfo(), healthClass, coverageAmount, coverageMonthlyRate, setLoading, getIsSubscribed, setQuoteResults, quoteLogIdCallback, isQuotingCoverageAmount);
    scrollToTop();
    setErrorMessage({ status: '', message: '' });
  };

  let debounce = null;
  const setQuoteTimer = () => {

    let isSubscribed = true;

    if (debounce) {
      clearTimeout(debounce);
    }

    debounce = setTimeout(() => runQuote(() => isSubscribed), 1000);

    return () => {
      clearTimeout(debounce);
      isSubscribed = false;
    };
  };

  useEffect(() => {
    if (basicInfoModalShow) {
      return;
    }

    if (!coverageAmount) {
      return;
    }

    return setQuoteTimer();
  }, [healthClass, coverageAmount, coverageMonthlyRate, props.basicInfo, isQuotingCoverageAmount]);

  const handleQuoteSelection = (quoteInfo, callbacks) => {
    const { onNext } = callbacks;
    const { selectedQuote } = quoteInfo;

    if (!selectedQuote.quoteLogId) {
      selectedQuote.quoteLogId = quoteLogId;
    };

    onNext();
  };

  const handleHealthClassChange = (event) => {
    setHealthClass(event);
  };

  const setOpenEditPopUp = (open) => {
    if (open) {
      setIsPurlVerification(false);
    }
    setBasicInfoModalShow(open);
  };

  const quoteOnClick = (quote) => {
    const basicInfo = getBasicInfo();
    const form = { ...props.fullFormState };
    form.pages.quoter.coverageAmount = isQuotingCoverageAmount ? coverageAmount : quote.coverageAmount;
    form.pages.quoter.termLength = termLength;
    form.pages.quoter.term = termLength;
    form.pages.quoter.healthClass = healthClass;
    form.pages.quoter.isSelectOrTalk = "quoteSelect";
    props.onMultiFieldChange(form);

    basicInfo.faceAmount = coverageAmount;
    basicInfo.termLength = termLength;
    basicInfo.healthClass = healthClass;
    setBasicInfo(basicInfo);

    const quoteInfo = {
      coverageAmount: coverageAmount,
      termLength: termLength,
      healthClass: healthClass,
      selectedQuote: quote,
    };

    const callbacks = {
      onNext: props.onNext,
    };

    handleQuoteSelection(quoteInfo, callbacks);
  };

  const filterResults = (quotes) => {
    return QuoterFilter.filterResults(filterObject, quotes);
  };

  const sortResults = (quotes) => {
    if (sortObject.lowestPrice) {
      quotes.sort((a, b) => a.monthlyRate - b.monthlyRate || b.totalUnweightedScore - a.totalUnweightedScore)[0];
    } else if (sortObject.highestPrice) {
      quotes.sort((a, b) => b.monthlyRate - a.monthlyRate || a.totalUnweightedScore - b.totalUnweightedScore)[0];
    } else if (sortObject.lowestCoverage) {
      quotes.sort((a, b) => a.coverageAmount - b.coverageAmount || a.totalUnweightedScore - b.totalUnweightedScore)[0];
    } else if (sortObject.highestCoverage) {
      quotes.sort((a, b) => b.coverageAmount - a.coverageAmount || a.totalUnweightedScore - b.totalUnweightedScore)[0];
    } else if (sortObject.highestRated) {
      quotes.sort((a, b) => b.companyRatingsScore - a.companyRatingsScore || a.totalUnweightedScore - b.totalUnweightedScore)[0];
    } else if (sortObject.fastest) {
      quotes.sort((a, b) => b.speedScore - a.speedScore || a.monthlyRate - b.monthlyRate || b.totalUnweightedScore - a.totalUnweightedScore)[0];
      quotes[0].isFastest = true;
    }

    return quotes;
  };

  useEffect(() => {
    if (quoteResults.length > 0) {
      quoteResults.sort((a, b) => b.speedScore - a.speedScore || a.monthlyRate - b.monthlyRate || b.totalUnweightedScore - a.totalUnweightedScore)[0];
      quoteResults[0].isFastest = true;
      setFastestRate(quoteResults[0].monthlyRate);
      quoteResults.sort((a, b) => a.monthlyRate - b.monthlyRate || b.totalUnweightedScore - a.totalUnweightedScore)[0];
      setlowestPrice(quoteResults[0].monthlyRate);
      quoteResults[0].isBestPrice = true;

      quoteResults.forEach(qr => {
        qr.isFeaturedCarrier = utmQuoterCarrierId === (qr.carrierID ? qr.carrierID : null);
      });

      quoteResults.sort((a, b) => sorter(a, b, (!!props.quoteResultSortDefault ? props.quoteResultSortDefault : (isQuotingCoverageAmount ? 'lowestPrice' : 'highestCoverage'))));
      const filteredQuotes = filterResults(quoteResults);
      setFilteredQuoteResults(sortResults(filteredQuotes));

    } else {
      setFilteredQuoteResults([]);
    }
  }, [quoteResults]);

  useEffect(() => {
    let quotesList = filterResults(quoteResults);
    if (quotesList.length) {
      quotesList = sortResults(quotesList);
    }
    setFilteredQuoteResults(quotesList);
  }, [filterObject, isSortedByPrice, sortObject]);

  const quoteLimit = (filteredQuoteResults.some(r => r.isFeaturedCarrier === true) ? 4 : 3);

  const toggleQuoteResults = () => {
    setDisplayAllQuotes(displayAllQuotes => !displayAllQuotes);
  };

  useEffect(() => {
    if (!displayAllQuotes && filteredQuoteResults.length > quoteLimit) {
      const limitedQuotes = filteredQuoteResults.slice(0, quoteLimit);
      const isFeaturedCarrierQuote = filteredQuoteResults.some(r => r.isFeaturedCarrier === true);
      const featuredCarrierIndex = isFeaturedCarrierQuote && filteredQuoteResults.findIndex(x => x.isFeaturedCarrier === true) || 0;
      if (isFeaturedCarrierQuote && featuredCarrierIndex >= quoteLimit) {
        let quoteList = filteredQuoteResults;
        quoteList = [...quoteList.slice(0, quoteLimit - 1), quoteList[featuredCarrierIndex]];
        setDisplayedQuoteResults(quoteList);
      }
      else {
        setDisplayedQuoteResults(limitedQuotes);
      }
    }
    else {
      setDisplayedQuoteResults(filteredQuoteResults);
    }
  }, [displayAllQuotes, filteredQuoteResults]);

  const handleBasicInfoSubmit = (open, purlMismatch) => {
    setBasicInfoModalShow(open);
    const basicInfo = getBasicInfo();
    setBasicInfo(basicInfo);
    if (isPurlVerification && !purlMismatch) {
      quoteOnClick(selectedQuote);
    } else {
      setQuoteTimer();
    }
  };

  const redirect = () => {
    props.onNext();
  };

  const getSpecialistHelp = () => {
    ContactSpecialist(props.fullFormState, coverageAmount, null, healthClass, quoteResults, redirect, props.onMultiFieldChange);
  };

  return (
    <Container className='pt-4 bg-color'>
      <Row className='final-expense-quoter condensed'>
        <header>
          <BasicInfoSummaryExtended
            coverageAmountDefault={coverageDefaults.coverageAmount}
            coverageAmountSpecs={coverageAmountSpecs}
            coverageAmount={coverageAmount}
            coverageMonthlyRateDefault={coverageDefaults.coverageMonthlyRate}
            coverageMonthlyRate={coverageMonthlyRate}
            isQuotingCoverageAmount={isQuotingCoverageAmount}
            setLoading={setLoading}
            setIsQuotingCoverageAmount={setIsQuotingCoverageAmount}
            setCoverageAmount={setCoverageAmount}
            setCoverageMonthlyRate={setCoverageMonthlyRate}
            marks={DigitalCoverageAmounts(null)}
            setHealthClass={setHealthClass}
            healthClass={healthClass}
            basicInfo={basicInfo}
            setOpenEditPopUp={setOpenEditPopUp}
            loading={loading}
            errorMessage={errorMessage}
            setErrorMessage={(value) => setErrorMessage(value)}
            quoteLogId={quoteLogId}
            onHealthClassChange={handleHealthClassChange}
          />
        </header>
        <Col lg={12}>
          <div className={`quote-results-grid-container`}>
            <Fragment>
              <div className="quote-results-header-start-container">
                <h3>Top Coverage Options
                  <span className="help-container" onClick={handleToolTip}>
                    <Tooltip
                      title={quoteResultsValueProp}
                      enterTouchDelay={0}
                      slotProps={{
                        tooltip: {
                          className: 'light-tooltip',
                          sx: { fontSize: '11px' }
                        },
                      }}
                    >
                      <span>
                        <InfoOutlinedIcon className="help-tooltip" />
                      </span>
                    </Tooltip>
                  </span>
                </h3>
                <p className="info">{quoteResultsValueProp}</p>
              </div>
              <div className="quote-results-header-end-container">
                <QuoterSortCondensed
                  quoteResultSortDefault={props.quoteResultSortDefault}
                  isSortedByPrice={isSortedByPrice}
                  setIsSortedByPrice={setSortedByPrice}
                  setIsSortObject={setSortObject}
                  isQuotingCoverageAmount={isQuotingCoverageAmount}
                  isDisabled={loading}
                  ref={sortRef}
                />
                <QuoterFiltersCondensed
                  isSortedByPrice={isSortedByPrice}
                  filterObject={filterObject}
                  setSortedByPrice={setSortedByPrice}
                  setFilterObject={setFilterObject}
                  isDisabled={loading}
                  ref={filterRef} />
              </div>
              <div className={`quote-results-content-container ${(displayedQuoteResults.length < 1 && !loading) ? "no-results" : ""}`}>
                <QuoteResults
                  isQuotingCoverageAmount={isQuotingCoverageAmount}
                  coverageAmount={coverageAmount}
                  results={displayedQuoteResults}
                  tobacco={basicInfo.tobacco}
                  loading={loading}
                  setSelectedQuote={quoteOnClick}
                  fullFormState={props.fullFormState}
                  onMultiFieldChange={props.onMultiFieldChange}
                  isSortedByPrice={isSortedByPrice}
                />
                {((filteredQuoteResults.length > quoteLimit)) && (!loading) &&
                  <div className="quote-results-toggle-container toggleBackground">
                    <button onClick={toggleQuoteResults} className="buttonClass">
                      {displayAllQuotes ? <RemoveCircleOutlineOutlinedIcon color='primary' className='icon-margin' /> :
                        <AddCircleOutlineOutlinedIcon color='primary' className='icon-margin' />}
                      <span
                        className="title darkenText">{displayAllQuotes ? "Show fewer quotes" : `${props.isAmerico ? (quotesWithoutAmerico.length - quoteLimit) : (filteredQuoteResults.length - quoteLimit)} more quotes`}</span>
                    </button>
                  </div>}
              </div>
            </Fragment>
            <div className="quote-results-footer-full-container">
              <Button variant='outlined' className="primary-bg-color contact-btn float-end" color='primary' size='medium'
                onClick={() => getSpecialistHelp()}>Contact A Specialist</Button>
            </div>

          </div>
          {isTestimonialPanelEnable && <Testimonials isShowCard={true} isShowPanel={true} />}
          <div className="promoted-container">
            {promotedProducts && (promotedProductCount > 0) && <CtaSolution data={promotedProducts} productType={props.productType} handlePromotedProduct={handlePromotedProduct} />}
          </div>
        </Col>
      </Row>

      {basicInfoModalShow &&
        <BasicInfoSummaryFinalExpenseModel
          openEditPopUp={basicInfoModalShow}
          closeModal={() => setBasicInfoModalShow(false)}
          setOpenEditPopUp={handleBasicInfoSubmit}
          isPurlVerification={isPurlVerification}
          formState={props.formState}
          product={'finalexpense'}
          selectedQuote={selectedQuote} />}
    </Container>
  )
};

function QuoteResults(props) {
  if (props.loading) {
    return (
      <div className='loading-container'>
        <LoadingGrid single={false} />
      </div>
    );
  }

  const filteredResults = props.results ? props.results.filter(r => r.monthlyRate > 0) : [];

  if (filteredResults.length < 1) {
    if (props.coverageAmount < 50000) {
      return <NoQuoteAvailable
        subText="Some values may not meet minimum requirements. Please update the coverage amount or term length, or click 'Show other options' below." />;
    }
    return <NoQuoteAvailable
      subText="Some limits may have been exceeded. Please select a shorter term duration or click 'Show other options' below." />;
  }
  return (
    <div className='final-expense-quote-result-section-container'>

      {filteredResults && filteredResults.map((r, i) =>
        <QuoteResult
          isQuotingCoverageAmount={props.isQuotingCoverageAmount}
          index={i}
          key={i}
          coverageAmount={props.coverageAmount}
          tobacco={props.tobacco}
          result={r}
          loading={props.loading}
          onSelectClicked={() => props.setSelectedQuote(r)}
          fullFormState={props.fullFormState}
          onMultiFieldChange={props.onMultiFieldChange}
          isSortedByPrice={props.isSortedByPrice}
        />
      )}
    </div>
  );
};

QuoteResults.propTypes = {
  results: PropTypes.array,
  coverageAmount: PropTypes.number,
  loading: PropTypes.bool,
  isSortedByPrice: PropTypes.bool,
  fullFormState: PropTypes.object,
  onMultiFieldChange: PropTypes.func,
  tobacco: PropTypes.string,
};

function QuoteResult(props) {
  const [expanded, setExpanded] = useState(false);
  const { result, onSelectClicked, index } = props;
  return (
    <div className='final-expense-quote-result-section'>
      {result &&
        <FinalExpenseQuoteCard
          index={index}
          isQuotingCoverageAmount={props.isQuotingCoverageAmount}
          carrierName={result.carrierName}
          productName={result.product}
          underwritingTime={result.underwritingTimeTypical}
          quote={result}
          expanded={expanded}
          expand={() => setExpanded(!expanded)}
          monthlyRate={result.monthlyRate}
          logoUrl={result.logoUrl}
          rating={result.rating}
          onClick={() => {
            const form = { ...props.fullFormState };
            form.pages.quoter.carrierName = result.carrierName;
            form.pages.quoter.carrierLogoUrl = result.logoUrl;
            form.pages.quoter.premium = result.monthlyRate;
            form.pages.quoter.product = result.product;
            form.pages.quoter.cashValue = result.cashValue !== null ? result.cashValue : null;
            form.pages.quoter.logoUrl = result.logoUrl;
            form.pages.quoter.selectedQuote = result;
            props.onMultiFieldChange(form);
            onSelectClicked();
          }}
          isBestPrice={result.isBestPrice}
          isFastest={result.isFastest}
          isDigitalProduct={result.isDigitalProduct}
          isSortedByPrice={props.isSortedByPrice}
          requiresPhoneInterview={result.requiresPhoneInterview}
          requiresMedicalExam={result.requiresMedicalExam}
          applicationInformation={result.applicationInformation}
          productHighlightInfo={result.productHighlightInfo}
          productDetails={<ProductDetails
            carrierName={result.carrierName}
            productDescription={result.productDescription}
            productInformation={result.productInformation}
            hasPermConversion={result.hasPermConversionOption}
          />}
        />
      }
    </div>
  );
}

QuoteResult.propTypes = {
  result: PropTypes.object,
  isSortedByPrice: PropTypes.bool,
  fullFormState: PropTypes.object,
  onMultiFieldChange: PropTypes.func,
  onSelectClicked: PropTypes.func,
  index: PropTypes.number,
};

Quoter.propTypes = {
  formState: PropTypes.object,
  basicInfo: PropTypes.object,
  multiTermResult: PropTypes.bool,
  fullFormState: PropTypes.object,
  onMultiFieldChange: PropTypes.func,
  onNext: PropTypes.func,
};

Quoter.DefaultFormState = (clientInfo = null) => {
  return {
    coverageAmount: (clientInfo && clientInfo.faceAmount) || 0,
    healthClass: (clientInfo && clientInfo.healthClass) || 'PP',
    isSelectOrTalk: "quoteSelect",
  };
};